/**
 * PhoneField — international phone input with country code picker.
 *
 * Visual contract: this component renders as a real MUI OutlinedInput
 * shell (FormControl + InputLabel + OutlinedInput + FormHelperText) so
 * it lines up byte-for-byte with the surrounding `<TextField>`s in any
 * dialog. The country picker is a `<Select variant="standard">` placed
 * in the OutlinedInput's `startAdornment`, so flag + dial code sit
 * INSIDE the same outlined border (no nested boxes).
 *
 * Storage contract: E.164 ('+8613800138000', '+14155551234'). Parsing
 * is delegated to libphonenumber-js (transitive dep via
 * react-phone-number-input). Server-side validation lives in
 * `svdy-platform/app/.../schemas.py` via the `phonenumbers` Python
 * library — see svdy-rules §10.2 (defense-in-depth).
 *
 * USAGE:
 *
 *   // Inside a Dialog whose other TextFields are default (medium) size:
 *   <PhoneField
 *     value={form.phone}
 *     onChange={(v) => setForm((p) => ({ ...p, phone: v ?? '' }))}
 *     label="Phone"
 *     fullWidth
 *     margin="dense"
 *   />
 *
 *   // Inside a compact dialog where surrounding TextFields use size="small":
 *   <PhoneField label="Phone" value={...} onChange={...} size="small" fullWidth />
 *
 *   // When an outer Typography is the label (legacy `styles.label` pattern in
 *   // OrganizationProfile / WorkerProfile section labels), pass label="":
 *   <Typography sx={styles.label}>Phone</Typography>
 *   <PhoneField label="" value={...} onChange={...} fullWidth margin="none" />
 *
 * E.164 examples:
 *   +8613800138000   (China mobile)
 *   +14155551234     (US)
 *   +442012345678    (UK)
 *
 * Per svdy-ui §13: PhoneField's mobile twin is svdy-mobile, but mobile
 * currently only displays phones (no input). Update both if mobile
 * grows an editable phone surface.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  type Country,
} from 'react-phone-number-input';
import { formatIncompletePhoneNumber } from 'libphonenumber-js';
import flags from 'react-phone-number-input/flags';
import enLabels from 'react-phone-number-input/locale/en.json';

interface PhoneFieldProps {
  /** E.164 string ('+8613800138000') or '' / undefined when empty */
  value: string | undefined;
  /** Called with E.164 or undefined when cleared */
  onChange: (value: string | undefined) => void;
  /** Floating label. Pass empty string to hide (when an outer Typography label is in use) */
  label?: string;
  /** ISO 3166-1 alpha-2 default region (US, CN, GB, ...). Used when value is empty or unparseable */
  defaultCountry?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  error?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  /** Default 'medium' to match the MUI TextField default. Pass 'small' inside compact dialogs. */
  size?: 'small' | 'medium';
  placeholder?: string;
}

// Sorted list of supported countries with their dial codes & display names.
// Computed once at module load.
const COUNTRY_OPTIONS: Array<{
  code: Country;
  callingCode: string;
  name: string;
}> = getCountries()
  .map((code) => ({
    code,
    callingCode: getCountryCallingCode(code),
    name: (enLabels as Record<string, string>)[code] || code,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * Render the country flag SVG. react-phone-number-input/flags exports a
 * map of {countryCode: ReactComponent}. Returns null if flag asset is
 * missing for a particular country (rare; fall back to country code text).
 */
const FlagFor: React.FC<{ country: Country; size?: number }> = ({ country, size = 20 }) => {
  const Flag = (flags as Record<string, React.ComponentType<{ title?: string }>>)[country];
  if (!Flag) {
    return (
      <Box sx={{ width: size, height: size * 0.7, fontSize: 10, color: 'text.disabled' }}>
        {country}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: size,
        height: size * 0.7,
        flexShrink: 0,
        '& svg, & img': { width: '100%', height: '100%', display: 'block' },
        // The lib renders <img> for some countries; <svg> for others. Both should fit.
      }}
    >
      <Flag title={(enLabels as Record<string, string>)[country] || country} />
    </Box>
  );
};

const PhoneField: React.FC<PhoneFieldProps> = ({
  value,
  onChange,
  label = 'Phone',
  defaultCountry = 'US',
  required = false,
  disabled = false,
  fullWidth = false,
  helperText,
  error = false,
  margin = 'none',
  size = 'medium',
  placeholder,
}) => {
  const labelId = React.useId();
  const selectId = React.useId();

  // Decompose external E.164 value into (country, national portion).
  // parsePhoneNumber may throw / return undefined on malformed input.
  const parsed = useMemo(() => {
    if (!value) return null;
    try {
      const p = parsePhoneNumber(value);
      return p && p.country ? p : null;
    } catch {
      return null;
    }
  }, [value]);

  const [country, setCountry] = useState<Country>(
    (parsed?.country as Country) || (defaultCountry as Country),
  );
  // National portion as a formatted string (e.g. "138 0013 8000" for CN).
  const [national, setNational] = useState<string>(() => {
    if (parsed) {
      return formatIncompletePhoneNumber(parsed.nationalNumber, parsed.country);
    }
    return '';
  });

  // Sync external value → internal state whenever the parent forces a new value
  // (e.g. dialog opens with prefilled data, form reset, etc).
  useEffect(() => {
    if (parsed && parsed.country) {
      setCountry(parsed.country as Country);
      setNational(formatIncompletePhoneNumber(parsed.nationalNumber, parsed.country));
    } else if (!value) {
      setNational('');
      // Don't reset country — let the user keep their pick if they're typing
      // from scratch in a particular region.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Compose E.164 from (country, raw national digits).
  const composeE164 = useCallback((c: Country, formattedNational: string): string | undefined => {
    const digits = formattedNational.replace(/\D/g, '');
    if (!digits) return undefined;
    const callingCode = getCountryCallingCode(c);
    return `+${callingCode}${digits}`;
  }, []);

  const handleCountryChange = (newCountry: Country) => {
    setCountry(newCountry);
    // Re-format the national portion under the new country's rules.
    const reformatted = formatIncompletePhoneNumber(national.replace(/\D/g, ''), newCountry);
    setNational(reformatted);
    onChange(composeE164(newCountry, reformatted));
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Format-as-you-type via libphonenumber-js. The function strips
    // non-digit characters internally and returns the country-specific
    // nationally-formatted display string (e.g. "(415) 555-1234" for US).
    const formatted = formatIncompletePhoneNumber(raw, country);
    setNational(formatted);
    onChange(composeE164(country, formatted));
  };

  // Country picker rendered as a Select inside the OutlinedInput's
  // startAdornment. variant="standard" + disableUnderline removes the
  // nested border — the only border visible is the surrounding
  // OutlinedInput's, which gives the "single field" feel.
  const countrySelect = (
    <Select
      id={selectId}
      value={country}
      onChange={(e) => handleCountryChange(e.target.value as Country)}
      disabled={disabled}
      variant="standard"
      disableUnderline
      MenuProps={{
        PaperProps: {
          sx: { maxHeight: 320, width: 280 },
        },
      }}
      sx={{
        // Keep the trigger compact — flag + dial code only.
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          padding: '0 !important',
          paddingRight: '20px !important', // room for the dropdown arrow
          minHeight: 'unset',
          fontSize: size === 'small' ? '0.8125rem' : '0.875rem',
          color: disabled ? 'text.disabled' : 'text.primary',
        },
        '& .MuiSelect-icon': {
          right: 0,
          color: disabled ? 'text.disabled' : 'text.secondary',
        },
        '&:before, &:after': { display: 'none' }, // belt-and-suspenders for underline
      }}
      renderValue={(c) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <FlagFor country={c as Country} size={size === 'small' ? 18 : 20} />
          <Typography
            component="span"
            sx={{
              fontSize: 'inherit',
              color: 'inherit',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            +{getCountryCallingCode(c as Country)}
          </Typography>
        </Box>
      )}
    >
      {COUNTRY_OPTIONS.map((opt) => (
        <MenuItem key={opt.code} value={opt.code} dense>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <FlagFor country={opt.code} size={20} />
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8125rem' }}>
              {opt.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}
            >
              +{opt.callingCode}
            </Typography>
          </Box>
        </MenuItem>
      ))}
    </Select>
  );

  // When label is the empty string we treat it as "no label" — caller is
  // using an outer Typography label (OrganizationProfile pattern). We
  // still must NOT render <InputLabel> in that case, otherwise MUI will
  // render an empty notch in the border.
  const hasLabel = label !== '' && label !== undefined;

  return (
    <FormControl
      variant="outlined"
      fullWidth={fullWidth}
      size={size}
      margin={margin}
      error={error}
      disabled={disabled}
      required={required}
    >
      {hasLabel && (
        <InputLabel htmlFor={labelId} shrink>
          {label}
        </InputLabel>
      )}
      <OutlinedInput
        id={labelId}
        // notched only when there's a label — otherwise we get an empty notch
        notched={hasLabel ? true : undefined}
        label={hasLabel ? label : undefined}
        value={national}
        onChange={handleNationalChange}
        placeholder={placeholder ?? (hasLabel ? '' : 'Phone number')}
        startAdornment={
          <InputAdornment position="start" sx={{ mr: 1, ml: -0.5 }}>
            {countrySelect}
          </InputAdornment>
        }
        inputProps={{
          'aria-label': label || 'Phone number',
          inputMode: 'tel',
          autoComplete: 'tel-national',
        }}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default PhoneField;
export { PhoneField };
